import { OktaAuth } from '@okta/okta-auth-js';
import isEqual from 'lodash/isEqual';
import { makeAutoObservable } from 'mobx';
import { PartialModes } from '../AltHome';
import Backend, {
    CreateConfigResponse,
    FileType,
    InjectInfo,
    ProcessorInfo,
    SaveCredsResponse,
} from '../backend/Backend';
import api from '../backend/DataSettingsBackend';
import { SalesforceApp, SalesforceDashboard, TriageCustomer, TriageProduct } from '../common/types';
import DataSourceType from '../DataSourceType';
import { DataSourceProps } from '../wizard/DataSource';
import { MappingItemProps } from '../wizard/MappingItem';

export type handleRunConfigProps = {
    isEdit: boolean;
    oktaAuth: OktaAuth;
    success_callback?: Function;
    fail_callback?: Function;
    inject_info?: InjectInfo;
    selected_app?: SalesforceApp;
    selected_dashboard?: SalesforceDashboard;
    newAppName?: string;
    ignoreErrors?: boolean;
    templateOnly?: boolean;
    processorOnly?: boolean;
    isTest?: boolean;
    isPartial?: boolean;
    partialMode?: PartialModes | undefined;
    processToDB?: boolean;
};

export class ConfigStore {
    injectOnly?: boolean = false;
    setinjectOnly = (inject: boolean) => {
        this.injectOnly = inject;
    };

    constructor() {
        makeAutoObservable(this);
    }

    show_progress: boolean = false;
    showProgress = (showProgress: boolean) => {
        this.show_progress = showProgress;
    };

    // source type
    source_type?: DataSourceProps;
    setSourceType = (source: DataSourceProps) => {
        this.source_type = source;
    };

    // selected goal
    selected_goal?: any;
    setSelectedGoal = (goal: any) => {
        this.selected_goal = goal;
    };

    // config name
    config_name?: string;
    setConfigName = (name: string) => {
        this.config_name = name;
    };

    // parent config id
    parent_config_id: string = '';
    setParentConfigId = (id: string) => {
        this.parent_config_id = id;
    };

    // environment id
    environment_id: string = '';
    setEnvironmentId = (id: string) => {
        this.environment_id = id;
    };

    // data processing config id
    data_processing_config_id: string = '';
    setDataProcessingConfigId = (id: string) => {
        this.data_processing_config_id = id;
    };

    // source creds
    sourceCreds: SaveCredsResponse;
    setSourceCreds = (creds: SaveCredsResponse) => {
        this.sourceCreds = creds;
    };

    // master files
    masterFiles: Array<File> | null;
    setMasterFiles = (files: Array<File> | null) => {
        this.masterFiles = files;
    };

    // lookup files
    lookupFiles: Array<File> | null;
    setLookupFiles = (files: Array<File> | null) => {
        this.lookupFiles = files;
    };

    // lookup config
    lookupConfig: any | null = [];
    setLookupConfig = (lookup_config: any | null) => {
        this.lookupConfig = lookup_config;
    };

    // data settings
    dataSettings: any;
    setDataSettings = (data_settings: any) => {
        this.dataSettings = data_settings;
    };
    setDataSettingsItem = (dataSettingsType: string, item: MappingItemProps) => {
        const dataSettingsItemIndex = this.dataSettings.config_mapping[dataSettingsType].findIndex(
            (currItem) => currItem.name === item.name,
        );

        if (
            dataSettingsItemIndex !== -1 &&
            !isEqual(item, this.dataSettings.config_mapping[dataSettingsType][dataSettingsItemIndex])
        ) {
            const updatedConfigMapping = [...this.dataSettings.config_mapping[dataSettingsType]];
            updatedConfigMapping[dataSettingsItemIndex] = item;

            const newDataSettings = {
                ...this.dataSettings,
                config_mapping: {
                    ...this.dataSettings.config_mapping,
                    [dataSettingsType]: updatedConfigMapping,
                },
            };

            this.setDataSettings(newDataSettings);
        }
    };

    getDataSettings = async (oktaAuth: OktaAuth, successCallback?: Function) => {
        await api.getDataSettingItems(oktaAuth, this.data_processing_config_id, this.parent_config_id).then((data) => {
            const dataSettingsResults = data.data_processing_config;

            Backend.getTriageCustomers(oktaAuth).then((data) => {
                const resultsData: TriageCustomer[] = data.data;
                const triageItems: MappingItemProps[] = dataSettingsResults.config_mapping.triage;
                const index = triageItems.findIndex((item) => item.name === 'triage_schema');
                if (index !== -1) {
                    triageItems[index].options = resultsData.slice().sort((a, b) => a.name.localeCompare(b.name));
                    triageItems[index].value = triageItems[index].value
                        ? resultsData.find((i) => i.name === triageItems![index].value.name) || ''
                        : triageItems[index].value || '';

                    Backend.getTriageProducts(oktaAuth).then((data) => {
                        const resultsData: TriageProduct[] = data.data;
                        const index = triageItems.findIndex((item) => item.name === 'triage_product_combination');
                        if (index !== -1) {
                            triageItems[index].options = resultsData;
                        }

                        dataSettingsResults.config_mapping.triage = triageItems;
                        this.setDataSettings(dataSettingsResults);
                        if (successCallback) {
                            successCallback();
                        }
                    });
                }
            });
        });
    };

    // template json
    template_json: any;
    setTemplateJson = (template: any) => {
        this.template_json = template;
    };

    // template config
    template_config: any;
    setTemplateConfig = (template_config: any) => {
        this.template_config = template_config;
    };

    // task id
    task_id: string | null = null;
    setNewTask = (task: string | null) => {
        this.task_id = task;
    };

    // start date
    startDate: Date | null = null;
    setStartDate = (date: Date) => {
        this.startDate = date;
    };

    // end date
    endDate: Date | null = null;
    setEndDate = (date: Date) => {
        this.endDate = date;
    };

    templateTestRun?: boolean = false;
    setTemplateTestRun = (templateTestRun: boolean) => {
        this.templateTestRun = templateTestRun;
    };

    lastTaskPerConfig?: any = {};
    setLastTaskPerConfig = (configId: string, lastTask: any) => {
        this.lastTaskPerConfig[configId] = lastTask;
    };

    processToDB?: boolean = false;
    setProcessToDB = (processToDB: boolean) => {
        this.processToDB = processToDB;
    };

    // process dialog
    processDialogOpen: boolean = false;
    processSuccessCallback: Function | undefined = undefined;
    processFailCallback: Function | undefined = undefined;
    processOnlyMode: boolean = false;
    isPartial: boolean = false;
    setProcessDialogOpen = (
        status: boolean,
        success_callback?: Function,
        fail_callback?: Function,
        processOnlyMode: boolean = false,
        isPartial: boolean = false,
        //processToDB: boolean = false
    ) => {
        console.log('got process dialog status: ', status);
        this.processDialogOpen = status;
        if (status === true) {
            this.processSuccessCallback = success_callback;
            this.processFailCallback = fail_callback;
            this.processOnlyMode = processOnlyMode;
            this.isPartial = isPartial;
            //this.processToDB = processToDB;
        } else {
            this.processSuccessCallback = undefined;
            this.processFailCallback = undefined;
            this.processOnlyMode = false;
            this.isPartial = false;
            this.processToDB = false;
        }
    };

    // csv partial mode
    partialMode: PartialModes | undefined = undefined;
    setPartialMode = (mode: PartialModes | undefined) => {
        this.partialMode = mode;
    };

    // date selection dialog
    openDateSelection: boolean = false;
    setOpenDateSelection = (status: boolean) => {
        this.openDateSelection = status;
    };

    loadConfigData = async (
        config: any,
        oktaAuth: OktaAuth,
        successCallback: Function,
        withDataSettings: boolean = true,
    ) => {
        // load current config data to store
        this.setSourceType({ name: config.config_type });
        this.setConfigName(config.config_name);
        this.setParentConfigId(config.rowid);
        this.setEnvironmentId(config.environments[0].rowid);
        this.setDataProcessingConfigId(config.environments[0].data_processing_config_id);
        this.setTemplateJson(config.environments[0].template.mapping_raw_json);
        this.setTemplateConfig(config);
        this.setProcessToDB(this.template_config.scop_project);

        if (withDataSettings) {
            await this.getDataSettings(oktaAuth, successCallback);
        } else if (successCallback) {
            successCallback();
        }
    };

    // create new / update existing config
    createConfig = async (oktaAuth: OktaAuth, successCallback?: Function, failCallback?: Function) => {
        const mappingJson = JSON.parse(this.template_json);

        if (this.parent_config_id === '') {
            this.template_config.run_config = false;
        }

        this.template_config.config_name = this.config_name;
        this.template_config.data_processing_config = this.dataSettings;
        this.template_config.environments[0].template.mapping = mappingJson;
        this.template_config.environments[0].template.mapping_raw_json = JSON.stringify(mappingJson, null, '\t');

        let configs_id: CreateConfigResponse = {
            parent_config_id: this.parent_config_id,
            data_processing_config_id: this.data_processing_config_id,
            task_id: '',
            environment_id: this.environment_id,
        };

        if (this.parent_config_id === '') {
            const new_config_result = await Backend.createConfig(this.template_config, oktaAuth);
            if (new_config_result?.data) {
                configs_id = new_config_result.data;
                if (successCallback) {
                    successCallback();
                }
            } else if (failCallback) {
                failCallback();
            }
        } else {
            const updateDataSettings = await api.updateDataSettings(
                oktaAuth,
                this.parent_config_id,
                this.data_processing_config_id,
                this.dataSettings,
                this.environment_id,
            );
            const updateTemplate = await Backend.updateTemplate(
                this.parent_config_id,
                JSON.parse(this.template_json),
                this.template_json,
                '',
                oktaAuth,
            );
            const updateConfig = await Backend.updateConfig(this.template_config, oktaAuth);

            if (updateDataSettings && updateTemplate && updateConfig && successCallback) {
                successCallback();
            } else if (failCallback) {
                failCallback();
            }
        }

        this.parent_config_id = configs_id.parent_config_id;
        this.data_processing_config_id = configs_id.data_processing_config_id;
        this.environment_id = configs_id.environment_id;

        return configs_id;
    };

    saveDataSource = async (oktaAuth: OktaAuth) => {
        if (
            this.source_type.name === DataSourceType.svmx ||
            this.source_type.name === DataSourceType.sfdc ||
            this.source_type.name === DataSourceType.custom
        ) {
            const sourceUserInfo = this.sourceCreds?.source_user_info;

            if (sourceUserInfo && sourceUserInfo.refresh_token && sourceUserInfo.instance_url) {
                await Backend.save_refresh_token(
                    this.parent_config_id,
                    this.sourceCreds.source_user_info.refresh_token,
                    this.sourceCreds.source_user_info.instance_url,
                    'Source',
                    oktaAuth,
                );
            }
        } else if (this.source_type.name === DataSourceType.csv) {
            if (this.masterFiles || this.lookupFiles) {
                await Promise.all([
                    Backend.saveFilesToStorage(this.masterFiles, FileType.master, this.parent_config_id, oktaAuth),
                    Backend.saveFilesToStorage(this.lookupFiles, FileType.lookup, this.parent_config_id, oktaAuth),
                ]);
            }
        }
    };

    // run config
    runConfig = async (
        isEdit: boolean,
        oktaAuth: OktaAuth,
        inject_info?: InjectInfo,
        selectedApp?: SalesforceApp,
        selectedDashboard?: SalesforceDashboard,
        newAppName?: string,
        startDate?: Date,
        endDate?: Date,
        ignoreErrors?: boolean,
        templateOnly?: boolean,
        partialMode?: PartialModes,
        isTest: boolean = false,
        processorOnly: boolean = false,
        isPartial: boolean = false,
        processToDB: boolean = false,
    ) => {
        const process_info: ProcessorInfo = {
            with_process: !templateOnly && !this.injectOnly,
            with_inject: !!inject_info,
            data: this.dataSettings,
            ignore_errors: ignoreErrors,
            process_to_db: processToDB,
            inject_data: inject_info
                ? {
                      inject_info,
                      app_name: newAppName && newAppName.length > 0 ? newAppName : inject_info.app_name,
                      app_id: selectedApp ? selectedApp.id : '',
                      templateId: selectedApp ? selectedApp.templateId : '',
                      templateVersion: selectedApp ? selectedApp.version : '',
                      dashboardId: selectedApp ? selectedDashboard.id : '',
                      dashboardName: selectedApp ? selectedDashboard.name : '',
                      newApp: !selectedApp,
                  }
                : undefined,
        };

        let new_task_details;
        const new_config_data: CreateConfigResponse = await this.createConfig(oktaAuth);
        this.setParentConfigId(new_config_data.parent_config_id);
        this.setDataProcessingConfigId(new_config_data.data_processing_config_id);
        this.setEnvironmentId(new_config_data.environment_id);

        switch (this.source_type.name) {
            case DataSourceType.svmx:
            case DataSourceType.sfdc:
            case DataSourceType.custom:
                if (!isEdit) {
                    await Backend.save_refresh_token(
                        new_config_data.parent_config_id,
                        this.sourceCreds.source_user_info.refresh_token,
                        this.sourceCreds.source_user_info.instance_url,
                        'Source',
                        oktaAuth,
                    );
                }

                new_task_details = await Backend.runConfig(
                    new_config_data.parent_config_id,
                    oktaAuth,
                    startDate,
                    endDate,
                    process_info,
                    isTest,
                    processorOnly,
                    isPartial,
                );
                new_task_details.data.task_id = new_task_details.data.rowid;
                break;
            case DataSourceType.csv:
                if (isEdit && processorOnly) {
                    // only case where we need to use the files on storage - no upload
                    const [mfiles, lfiles] = await Backend.getFilesPaths(this.parent_config_id, oktaAuth);
                    new_task_details = await Backend.doCsvFromStorage(
                        mfiles.map((file) => ({ file_path: file })),
                        lfiles.map((file) => ({ file_path: file })),
                        this.parent_config_id,
                        process_info,
                        oktaAuth,
                        isTest,
                        processorOnly,
                    );
                } else {
                    new_task_details = await Backend.doCsvUpload(
                        this.masterFiles,
                        this.lookupFiles,
                        new_config_data.parent_config_id,
                        process_info,
                        oktaAuth,
                        isTest,
                        processorOnly,
                        partialMode,
                    );
                }
                break;
            default:
                console.error(this.source_type.name, 'is not supported type');
                break;
        }
        //this.processToDB = false;
        return new_task_details;
    };

    handleRunConfig = async ({
        isEdit,
        oktaAuth,
        success_callback,
        fail_callback,
        inject_info,
        selected_app,
        selected_dashboard,
        newAppName,
        ignoreErrors = false,
        templateOnly = false,
        isTest = false,
        processorOnly = false,
        isPartial = false,
        partialMode = undefined,
        processToDB = false,
    }: handleRunConfigProps) => {
        const new_task = await this.runConfig(
            isEdit,
            oktaAuth,
            inject_info,
            selected_app,
            selected_dashboard,
            newAppName,
            this.startDate,
            this.endDate,
            ignoreErrors,
            templateOnly,
            partialMode,
            isTest,
            processorOnly,
            isPartial,
            processToDB,
        );
        if (new_task && new_task.data && new_task.data.task_id) {
            this.setNewTask(new_task.data.task_id);
            if (success_callback) {
                success_callback();
            }
        } else if (fail_callback) {
            fail_callback();
        }
    };

    handleRunTemplateTest = async (oktaAuth: OktaAuth, success_callback?: Function, fail_callback?: Function) => {
        const handleRunConfigProps: handleRunConfigProps = {
            isEdit: true,
            oktaAuth,
            success_callback: success_callback && success_callback(),
            fail_callback: fail_callback && fail_callback(),
            templateOnly: true,
            isTest: true,
        };
        const response = await this.handleRunConfig(handleRunConfigProps);
        return response;
    };

    resetConfigData = () => {
        this.setConfigName(undefined);
        this.setSourceType(null);
        this.setSourceCreds(null);
        this.setParentConfigId('');
        this.setEnvironmentId('');
        this.setDataProcessingConfigId('');
        this.setTemplateJson(null);
        this.setMasterFiles([]);
        this.setLookupFiles([]);
        this.setTemplateConfig(null);
        this.setDataSettings(null);
        this.setNewTask(null);
        this.setStartDate(null);
        this.setEndDate(null);
        this.setPartialMode(undefined);
        this.setProcessToDB(false);
    };
}

const configStore = new ConfigStore();

export default configStore;
